/** Client reading library of approved KPMG publications, never client documents. */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Typography, Box, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, CircularProgress, Alert, Button, Dialog,
  DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import { Visibility, Lock } from '@mui/icons-material';
import { authFetch } from '../../../api/authFetch';
import RefreshButton from '../../../components/RefreshButton';
import PortalTooltip from '../../../components/PortalTooltip';

interface AssetItem {
  id: string;
  filename: string;
  description: string | null;
  assessment_type: string;
  asset_class: string;
}

interface AssetPreview {
  sections: { title: string; text: string }[];
  image: string | null;
}

const AssetLibrary: React.FC = () => {
  const [assets, setAssets] = useState<AssetItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [viewingAsset, setViewingAsset] = useState<AssetItem | null>(null);
  const [preview, setPreview] = useState<AssetPreview | null>(null);
  const [viewError, setViewError] = useState<string | null>(null);
  const [viewLoading, setViewLoading] = useState(false);
  const previewRequest = useRef(0);

  const fetchAssets = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authFetch('/api/client/assets');
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to load assets');
      setAssets(data.assets);
      setLastRefreshed(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load assets');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAssets(); }, [fetchAssets]);

  const closePreview = () => {
    previewRequest.current += 1;
    setViewingAsset(null);
    setPreview(null);
    setViewError(null);
  };

  const handleViewAsset = async (asset: AssetItem) => {
    const requestId = ++previewRequest.current;
    setViewingAsset(asset);
    setPreview(null);
    setViewError(null);
    setViewLoading(true);
    try {
      const res = await authFetch(`/api/client/assets/${encodeURIComponent(asset.id)}/preview`);
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to open asset');
      if (requestId === previewRequest.current) setPreview(data);
    } catch (err) {
      if (requestId === previewRequest.current) {
        setViewError(err instanceof Error ? err.message : 'Failed to open asset');
      }
    } finally {
      if (requestId === previewRequest.current) setViewLoading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <PortalTooltip tooltipKey="client.page.read_only_library" showIcon>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>Asset Library</Typography>
        </PortalTooltip>
        <RefreshButton onRefresh={fetchAssets} loading={loading} lastRefreshed={lastRefreshed} />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Read approved KPMG assessment materials. These publications are view-only.
        To send your own supporting documents to KPMG, use Upload Documents.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : assets.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No approved assets are available for your assessment domains yet.</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Document</TableCell><TableCell>Assessment Type</TableCell>
                <TableCell>Asset Class</TableCell><TableCell>Access</TableCell><TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {assets.map(asset => (
                <TableRow key={asset.id}>
                  <TableCell>
                    <Typography variant="body2">{asset.filename}</Typography>
                    {asset.description && <Typography variant="caption">{asset.description}</Typography>}
                  </TableCell>
                  <TableCell>{asset.assessment_type}</TableCell>
                  <TableCell>{asset.asset_class.replace(/_/g, ' ')}</TableCell>
                  <TableCell><Chip label="View Only" size="small" color="warning" icon={<Lock />} /></TableCell>
                  <TableCell>
                    <Button size="small" startIcon={<Visibility />} onClick={() => handleViewAsset(asset)}>View</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Dialog open={!!viewingAsset} onClose={closePreview} maxWidth="md" fullWidth>
        <DialogTitle>Viewing: {viewingAsset?.filename}</DialogTitle>
        <DialogContent sx={{ '@media print': { display: 'none' } }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            View-only reading preview. Original formatting, charts and embedded objects may not appear.
            Downloading the original file is not available.
          </Alert>
          {viewLoading && <CircularProgress />}
          {viewError && <Alert severity="error">{viewError}</Alert>}
          {preview?.sections.map((section, index) => (
            <Paper key={index} variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6">{section.title}</Typography>
              <Typography component="pre" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', fontFamily: 'inherit' }}>
                {section.text}
              </Typography>
            </Paper>
          ))}
          {preview?.image && <Box component="img" src={preview.image} alt={viewingAsset?.filename} sx={{ maxWidth: '100%' }} />}
        </DialogContent>
        <DialogActions><Button onClick={closePreview}>Close</Button></DialogActions>
      </Dialog>
    </Box>
  );
};

export default AssetLibrary;
